import { EnvVariables } from "@/env-variables";
import { analytics } from "@/lib/analytics";
import { getCurrentRoutePath, getHttpRequestMeta } from "@/lib/analytics/api-utils";
import { addSentryBreadcrumb } from "@/lib/sentry";
import {
  createFeRequestId,
  FE_REQUEST_ID_HEADER,
  FE_REQUEST_ID_PROPERTY,
  isHttpLikeRequestUrl,
} from "./request-id";

const FETCH_INSTRUMENTATION_KEY = "__FE_REQUEST_ID_FETCH_INSTRUMENTED__";

interface FetchRequestIdInstrumentationOptions {
  skipRequestIdHeaderUrls?: string[];
}

const nowMs = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

let requestIdAllowedOrigins: Set<string> | undefined;

const getBaseUrl = () =>
  typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "http://localhost";

const getParsedUrl = (url: string) => {
  try {
    return new URL(url, getBaseUrl());
  } catch {
    return null;
  }
};

const getAbsoluteUrl = (url: string): string | null => getParsedUrl(url)?.toString() || null;

const getOrigin = (url?: string) => (url ? getParsedUrl(url)?.origin : undefined);

const isString = (value: string | undefined): value is string => Boolean(value);

const getRequestIdAllowedOrigins = () => {
  if (requestIdAllowedOrigins) return requestIdAllowedOrigins;

  requestIdAllowedOrigins = new Set(
    [
      getBaseUrl(),
      EnvVariables.apiUrl,
      EnvVariables.apiUrlOrganization,
      EnvVariables.apiUrlPartner,
      EnvVariables.apiUrlDocuments,
      EnvVariables.apiUrlAppointments,
      EnvVariables.apiUrlBlockTimeSlots,
      EnvVariables.apiUrlApptSmsReminder,
      EnvVariables.apiUrlApptSmsReminderTrigger,
    ]
      .map(getOrigin)
      .filter(isString),
  );

  return requestIdAllowedOrigins;
};

const getFetchUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  return String(input);
};

const getFetchMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
  if (init?.method) return init.method;
  if (typeof Request !== "undefined" && input instanceof Request) return input.method;
  return "GET";
};

const isS3Hostname = (hostname: string): boolean => {
  const host = hostname.toLowerCase();
  if (!host.endsWith(".amazonaws.com") && host !== "s3.amazonaws.com") {
    return false;
  }

  return (
    host.includes(".s3.") ||
    host.includes(".s3-accesspoint.") ||
    host.startsWith("s3.") ||
    host === "s3.amazonaws.com"
  );
};

const isAuthProviderRequest = (url: URL): boolean => {
  const hostname = url.hostname.toLowerCase();
  const pathname = url.pathname.toLowerCase();

  return (
    pathname.includes("/internal/sso/token") ||
    pathname.endsWith("/oauth2/token") ||
    hostname.includes("amazoncognito.com") ||
    (hostname.includes("cognito-idp.") && hostname.endsWith("amazonaws.com"))
  );
};

const shouldSkipConfiguredUrl = (url: URL, skipUrls: string[] = []) =>
  skipUrls.some((skipUrl) => getAbsoluteUrl(skipUrl) === url.toString());

const shouldAttachRequestIdHeader = (url: string, skipUrls: string[] = []): boolean => {
  const parsed = getParsedUrl(url);

  if (!parsed || isAuthProviderRequest(parsed) || isS3Hostname(parsed.hostname)) {
    return false;
  }

  if (shouldSkipConfiguredUrl(parsed, skipUrls)) {
    return false;
  }

  return getRequestIdAllowedOrigins().has(parsed.origin);
};

const withRequestIdHeader = (input: RequestInfo | URL, init: RequestInit | undefined) => {
  const requestId = createFeRequestId();
  const sourceHeaders =
    init?.headers ||
    (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined);
  const headers = new Headers(sourceHeaders);
  headers.set(FE_REQUEST_ID_HEADER, requestId);

  return {
    init: {
      ...init,
      headers,
    },
    requestId,
  };
};

export const installFetchRequestIdInstrumentation = (
  options: FetchRequestIdInstrumentationOptions = {},
) => {
  if (typeof globalThis.fetch !== "function") return;

  const instrumentedGlobal = globalThis as typeof globalThis & {
    [FETCH_INSTRUMENTATION_KEY]?: boolean;
  };

  if (instrumentedGlobal[FETCH_INSTRUMENTATION_KEY]) return;
  instrumentedGlobal[FETCH_INSTRUMENTATION_KEY] = true;

  const originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = async (input, init) => {
    const url = getFetchUrl(input);

    if (!isHttpLikeRequestUrl(url)) {
      return originalFetch(input, init);
    }

    const method = getFetchMethod(input, init);
    const startedAt = nowMs();
    const routePath = getCurrentRoutePath();
    const requestMeta = getHttpRequestMeta(url, method);
    const shouldAttachRequestId = shouldAttachRequestIdHeader(url, options.skipRequestIdHeaderUrls);
    const { init: requestInit, requestId } = shouldAttachRequestId
      ? withRequestIdHeader(input, init)
      : { init, requestId: undefined };

    addSentryBreadcrumb("fetch.request", url, {
      ...requestMeta,
      route_path: routePath,
      ...(requestId ? { [FE_REQUEST_ID_PROPERTY]: requestId } : {}),
    });

    try {
      const response = await originalFetch(input, requestInit);
      const duration = nowMs() - startedAt;

      analytics.trackPerformance({
        metricName: "fetch.duration",
        duration,
        metadata: {
          api_path: requestMeta.path,
          api_host: requestMeta.host,
          api_method: requestMeta.method,
          api_status: response.status,
          api_success: response.ok,
          route_path: routePath,
          ...(requestId ? { [FE_REQUEST_ID_PROPERTY]: requestId } : {}),
        },
      });

      return response;
    } catch (error) {
      const duration = nowMs() - startedAt;

      analytics.trackPerformance({
        metricName: "fetch.duration",
        duration,
        metadata: {
          api_path: requestMeta.path,
          api_host: requestMeta.host,
          api_method: requestMeta.method,
          api_success: false,
          route_path: routePath,
          ...(requestId ? { [FE_REQUEST_ID_PROPERTY]: requestId } : {}),
        },
      });

      addSentryBreadcrumb("fetch.error", url, {
        ...requestMeta,
        route_path: routePath,
        ...(requestId ? { [FE_REQUEST_ID_PROPERTY]: requestId } : {}),
      });

      throw error;
    }
  };
};
